import type { PatientBaseProfile } from '../types';

const EMPTY_PROFILE: PatientBaseProfile = {
  biologicalSex: null,
  dateOfBirth: null,
  heightCm: null,
};

export const MIN_BIRTH_YEAR = 1900;

export const getMaxBirthYear = (): number => new Date().getFullYear();

/** Canonical storage for year-only birth: YYYY-01-01 (spannometric age). */
export const birthYearToDateOfBirth = (year: number): string =>
  `${year}-01-01`;

export const parseBirthYear = (
  value?: string | number | null
): number | null => {
  const maxYear = getMaxBirthYear();

  if (typeof value === 'number' && Number.isInteger(value)) {
    return value >= MIN_BIRTH_YEAR && value <= maxYear ? value : null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }

  const yearOnly = trimmed.match(/^(\d{4})$/);
  if (yearOnly != null) {
    const year = Number(yearOnly[1]);
    return year >= MIN_BIRTH_YEAR && year <= maxYear ? year : null;
  }

  const fullDate = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (fullDate != null) {
    const year = Number(fullDate[1]);
    return year >= MIN_BIRTH_YEAR && year <= maxYear ? year : null;
  }

  return null;
};

export const isRegisteredBiologicalSex = (
  value: PatientBaseProfile['biologicalSex'] | undefined
): value is 'male' | 'female' => value === 'male' || value === 'female';

export const isRegisteredDateOfBirth = (
  value?: string | null
): value is string => parseBirthYear(value) != null;

export const isRegisteredHeight = (value?: number | null): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

export const resolvePatientBaseProfile = (
  patientProfile?: PatientBaseProfile | null
): PatientBaseProfile => ({
  biologicalSex: patientProfile?.biologicalSex ?? null,
  dateOfBirth: patientProfile?.dateOfBirth ?? null,
  heightCm: patientProfile?.heightCm ?? null,
});

export const EMPTY_PATIENT_BASE_PROFILE = EMPTY_PROFILE;

export const isPatientBaseProfileComplete = (
  profile: PatientBaseProfile
): boolean =>
  isRegisteredBiologicalSex(profile.biologicalSex) &&
  isRegisteredDateOfBirth(profile.dateOfBirth) &&
  isRegisteredHeight(profile.heightCm);

export const getMissingPatientBaseProfileLabels = (
  profile: PatientBaseProfile
): string[] => {
  const missing: string[] = [];

  if (!isRegisteredBiologicalSex(profile.biologicalSex)) {
    missing.push('sesso biologico');
  }
  if (!isRegisteredDateOfBirth(profile.dateOfBirth)) {
    missing.push('anno di nascita');
  }
  if (!isRegisteredHeight(profile.heightCm)) {
    missing.push('altezza');
  }

  return missing;
};

export type PatientProfileStatus = 'complete' | 'partial' | 'empty';

export const getPatientProfileStatus = (
  profile: PatientBaseProfile
): PatientProfileStatus => {
  if (isPatientBaseProfileComplete(profile)) {
    return 'complete';
  }

  const hasAnyField =
    isRegisteredBiologicalSex(profile.biologicalSex) ||
    isRegisteredDateOfBirth(profile.dateOfBirth) ||
    isRegisteredHeight(profile.heightCm);

  return hasAnyField ? 'partial' : 'empty';
};
